/* eslint-disable import/no-cycle */
import router from '@/router';
/* eslint-disable import/no-cycle */
import { useAlertsStore } from '@/modules/alerts/store/alertsStore';
import { useDriveStore } from '@/modules/drive/store/driveStore';
import { useMessagesStore } from '@/modules/messages/store/messagesStore';
import { useSpacesStore } from '@/modules/spaces/store/spacesStore';
import { useMainStore } from '@/store/mainStore';
import { MESSAGE_CATEGORY } from '@/types/enums';
import { IResponse } from '@/types/interfaces';
import ApiService from './ApiService';
import WebsocketService from './WebsocketService';
import MonitoringService from '@/services/MonitoringService';

export default class NativeService {
  public static awakeCallback: any = null

  private static fetchNetworkData() {
    const alertsStore = useAlertsStore();
    const spacesStore = useSpacesStore();
    const driveStore = useDriveStore();
    const messagesStore = useMessagesStore();
    const mainStore = useMainStore();

    alertsStore.fetchNotifications();

    spacesStore.fetchInvitationsContact();
    spacesStore.fetchInvitationsGroup();
    spacesStore.fetchRequestsToMyGroups();
    spacesStore.fetchSuggestions();
    spacesStore.fetchMyContacts();
    spacesStore.fetchOutgoingContact();
    spacesStore.fetchOutgoingGroup();
    spacesStore.fetchMyGroups();
    spacesStore.fetchUsersGroups();

    driveStore.fetchNetworkCases();
    driveStore.fetchNetworkPhotos();
    driveStore.fetchNetworkMixedData();

    messagesStore.fetchConversations();
    messagesStore.fetchGroupsForChat();
    messagesStore.updateContactStatusForCurrentConversation(); // CASE-2430

    mainStore.fetchUserBadges();
  }

  public static setSystemBadges(badgeAmount = 0): Promise<IResponse> {
    return ApiService.postAndReturnData('/user/notifications/set-system-badges', { badgeAmount });
  }

  public static onSleep(): void {
    console.log('sleep');
  }

  public static onAwake(): Promise<void> {
    const mainStore = useMainStore();
    // const messagesStore = useMessagesStore();
    if (mainStore.user?.accountId) {
      WebsocketService.closeSocket(); // it will automatically reconnect WebSocket
      NativeService.fetchNetworkData();
      // messagesStore.toBeSync = true;
      NativeService.setIconBadge(0);
    }

    if (this.awakeCallback) {
      this.awakeCallback();
    }

    console.log('awake');

    return Promise.resolve();
  }

  public static setToken(token: string) {
    const mainStore = useMainStore();
    mainStore.mobileToken = token;
    MonitoringService.logEvents({
      logType: 'setMobileToken',
      userId: mainStore.user?.accountId,
      key: 'NATIVE_CALL',
      id: mainStore.mobileToken,
    });
    return ApiService.postAndReturnData('/user/save-mobile-token', { token }).then((response) => {
      if (response.success) {
        // NativeService.setSystemBadges(0);
      }
    });
  }

  public static setPhotoLibStatus(status: boolean): Promise<void> {
    console.log('setPhotoLibStatus', status);

    return Promise.resolve();
  }

  public static connectToNative(): void {
    const { nativeApp } = window as any;
    const mainStore = useMainStore();

    const connect = () => {
      try {
        nativeApp.getPushToken('get');
        mainStore.isNative = true;
        nativeApp.getPhotoLibStatus('');
        NativeService.topColorHex('#fafaff');
      } catch (e) {
        setTimeout(() => {
          connect();
        }, 500);
      }
    };

    if (nativeApp) {
      connect();
    }
  }

  public static downloadMedia(url: string): void {
    const { nativeApp } = window as any;

    if (nativeApp) {
      nativeApp.saveToGalleryOneItem(url);
    }
  }

  public static downloadFile(url: string, fileName: string): void {
    const { nativeApp } = window as any;

    if (nativeApp) {
      nativeApp.downloadFileFromUrl(url, fileName);
    }
  }

  public static setIconBadge(badgeAmount = 0): void {
    const { nativeApp } = window as any;

    if (nativeApp) {
      nativeApp.setIconBadge(badgeAmount.toString());
    }
  }

  public static topColorHex(color: string): void {
    const { nativeApp } = window as any;

    if (nativeApp) {
      nativeApp.topColorHex(color);
    }
  }

  public static shareApp(): void {
    const mainStore = useMainStore();
    const { nativeApp } = window as any;

    if (nativeApp) {
      nativeApp.tellFriends(`${mainStore.user.firstName} ${mainStore.user.lastName} invited you to join CaseDrive. Download the app and connect with ${mainStore.user.firstName} today! https://apps.apple.com/ua/app/casedrive/id6443547638`);
    }
  }

  public static shareOnInstagram(url: string, type: string): void {
    const { nativeApp } = window as any;
    const mainStore = useMainStore();

    if (nativeApp && mainStore.isAndroid) {
      const imageURL = `"url":"${url}","type":${type}`; // change response due android issues
      nativeApp.shareOnInstagram(imageURL);
    }
    if (nativeApp && !mainStore.isAndroid) {
      nativeApp.shareOnInstagram({ url, type });
    }
  }

  public static getPdfUrl(url: string): void {
    const { nativeApp } = window as any;
    if (nativeApp) {
      nativeApp.getPdfUrl(url);
    }
  }

  public static toggleDialogNotifications(senderId: string, category: MESSAGE_CATEGORY, active: 'on' | 'off') {
    console.log('toggleDialogNotifications', ', senderId: ', senderId, ' category:', category, ', active: ', active);
    const { nativeApp } = window as any;

    if (nativeApp) {
      nativeApp.toggleNotifications(senderId, category, active);
    }
  }

  public static ActivateVoice(): void {
    const { nativeApp } = window as any;
    if (nativeApp) {
      nativeApp.checkSpeechRecognizer('');
    }
  }

  public static gotoPage(key: string, id = '') {
    console.log('gotoPage', ', key: ', key, ', id: ', id);
    const mainStore = useMainStore();
    MonitoringService.logEvents({
      logType: 'gotoPage', userId: mainStore.user?.accountId, key, id,
    });

    const messagesStore = useMessagesStore();

    if (key === 'DIRECT_MESSAGING' && id) {
      router.push(`/messages/${id}`);
      // messagesStore.fetchConversations().then(() => {
      //   // messagesStore.toBeSync = true;
      //   router.push(`/messages/${id}`);
      // });
    } else if (key === 'DIRECT_MESSAGING') {
      router.push('/messages/my');
    }

    if (key === 'GROUP_MESSAGING' && id) {
      router.push(`/messages/group/${id}`);
      // messagesStore.fetchConversations().then(() => {
      //   // messagesStore.toBeSync = true;
      //   router.push(`/messages/group/${id}`);
      // });
    } else if (key === 'GROUP_MESSAGING') {
      router.push('/messages/my');
    }

    if (key === 'FILE_DETAILS') {
      router.push('/drive/network');
    }
    if (key === 'CASE_DETAILS' && id) {
      router.push(`/drive/case/network/${id}/view/`);
    } else if (key === 'CASE_DETAILS') {
      router.push('/drive/network/cases');
    }
    if (!key) {
      router.push('/');
    }
  }
}
